<script setup>
import { computed, ref } from 'vue';
import { useStore } from 'dashboard/composables/store';
import { useAlert } from 'dashboard/composables';
import { useI18n } from 'vue-i18n';

import Button from 'dashboard/components-next/button/Button.vue';
import Input from 'dashboard/components-next/input/Input.vue';
import Select from 'dashboard/components-next/select/Select.vue';

const props = defineProps({
  botId: {
    type: Number,
    required: true,
  },
  decision: {
    type: Object,
    required: true,
  },
  routeOptions: {
    type: Array,
    default: () => [],
  },
});

const emit = defineEmits(['saved']);

const { t } = useI18n();
const store = useStore();

const feedback = ref(
  props.decision.feedback !== 'none' ? props.decision.feedback : ''
);
const feedbackRoute = ref(
  props.decision.feedback_route || props.decision.route || ''
);
const feedbackNote = ref(props.decision.feedback_note || '');
const isSaving = ref(false);

const routeChoices = computed(() => [
  { value: '', label: t('AI_AGENT.INTENT.FEEDBACK.NO_ROUTE') },
  ...props.routeOptions,
]);

const isWrong = computed(() => feedback.value === 'wrong');

const canSubmit = computed(
  () =>
    !!feedback.value &&
    (!isWrong.value || !!feedbackRoute.value) &&
    !isSaving.value
);

const submit = async () => {
  if (!canSubmit.value) return;
  isSaving.value = true;
  try {
    const saved = await store.dispatch('aiBotIntentDecisions/feedback', {
      botId: props.botId,
      id: props.decision.id,
      feedback: feedback.value,
      feedback_route: isWrong.value ? feedbackRoute.value : null,
      feedback_note: feedbackNote.value || null,
    });
    if (saved) {
      useAlert(t('AI_AGENT.INTENT.FEEDBACK.SAVED'));
      emit('saved', saved);
    }
  } finally {
    isSaving.value = false;
  }
};

defineExpose({ feedback, feedbackRoute, feedbackNote, canSubmit, submit });
</script>

<template>
  <div
    class="flex flex-col gap-3 rounded-lg bg-n-solid-1 p-3"
    data-testid="intent-feedback-form"
  >
    <span class="text-body font-medium text-n-slate-12">
      {{ t('AI_AGENT.INTENT.FEEDBACK.TITLE') }}
    </span>
    <div class="flex flex-wrap items-end gap-3">
      <div class="w-36">
        <Select
          v-model="feedback"
          :options="[
            { value: 'correct', label: t('AI_AGENT.INTENT.FEEDBACK.CORRECT') },
            { value: 'wrong', label: t('AI_AGENT.INTENT.FEEDBACK.WRONG') },
          ]"
          :placeholder="t('AI_AGENT.INTENT.FEEDBACK.SELECT')"
          data-testid="feedback-value"
        />
      </div>
      <div v-if="isWrong" class="w-52">
        <Select
          v-model="feedbackRoute"
          :options="routeChoices"
          :placeholder="t('AI_AGENT.INTENT.FEEDBACK.ROUTE_PLACEHOLDER')"
          data-testid="feedback-route"
        />
      </div>
      <div class="min-w-64 flex-1">
        <Input
          v-model="feedbackNote"
          :placeholder="t('AI_AGENT.INTENT.FEEDBACK.NOTE_PLACEHOLDER')"
          data-testid="feedback-note"
        />
      </div>
      <Button
        :disabled="!canSubmit"
        :is-loading="isSaving"
        :label="t('AI_AGENT.INTENT.FEEDBACK.SUBMIT')"
        sm
        data-testid="feedback-submit"
        @click="submit"
      />
    </div>
  </div>
</template>
